import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { IconName } from '../shared/components/Icon';
import { zoneFromPoint, type DropZone, type Rect } from './dockModel';
import { useDockElements, useWorkspace, useWorkspaceActions } from './WorkspaceProvider';
import type { DockTarget, WorkspaceState } from './workspaceState';

/*
 * Dragging, from the pointer's side.
 *
 * One gesture serves both a tab and a whole floating window: press, move past a few
 * pixels so a click is still a click, and from then on the pointer is asked, on every
 * frame, what it is over. The answer is a `DockTarget` the reducer already understands
 * plus the rectangle to paint, so the preview the operator sees and the arrangement
 * they get are computed by the same function.
 */

/** Distance the pointer must travel before a press becomes a drag. */
const dragThreshold = 5;
/** Width of the band along the canvas edge that docks against the whole workspace. */
const canvasEdgeBand = 22;

export type DropHint = 'tab' | 'split' | 'edge' | 'float';

export interface DragSubject {
  readonly kind: 'panel' | 'window';
  readonly id: string;
  readonly label: string;
  readonly icon: IconName;
  /** Where inside the dragged thing the pointer took hold, so a window does not jump. */
  readonly grab: { readonly x: number; readonly y: number };
  readonly size: { readonly width: number; readonly height: number } | null;
}

export interface DropResolution {
  readonly target: DockTarget;
  /** Viewport coordinates, so the overlay can paint without knowing the tree. */
  readonly preview: Rect;
  readonly hint: DropHint;
  readonly hostId: string | null;
  readonly zone: DropZone | null;
  readonly tabIndex: number | null;
}

export interface DockDragState {
  readonly subject: DragSubject;
  readonly pointer: { readonly x: number; readonly y: number };
  readonly resolution: DropResolution | null;
}

interface DockDragApi {
  readonly drag: DockDragState | null;
  readonly startPanelDrag: (event: ReactPointerEvent, subject: Omit<DragSubject, 'kind' | 'grab' | 'size'>) => void;
  readonly startWindowDrag: (event: ReactPointerEvent, subject: Omit<DragSubject, 'kind' | 'grab' | 'size'>, rect: Rect) => void;
}

const DockDragContext = createContext<DockDragApi | null>(null);

export function useDockDrag(): DockDragApi {
  const value = useContext(DockDragContext);
  if (value === null) throw new Error('useDockDrag was used outside the workspace canvas.');
  return value;
}

function toRect(rect: DOMRect): Rect {
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
}

export function clamp(value: number, lowest: number, highest: number): number {
  return Math.max(lowest, Math.min(value, highest));
}

function contains(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/** The slice of `rect` a document would take if dropped on `zone`. */
export function previewForZone(rect: Rect, zone: DropZone, rtl: boolean): Rect {
  if (zone === 'center') return rect;
  if (zone === 'top') return { ...rect, height: rect.height / 2 };
  if (zone === 'bottom') return { ...rect, y: rect.y + rect.height / 2, height: rect.height / 2 };
  const physicalStart = zone === 'start' ? !rtl : rtl;
  const width = rect.width / 2;
  return physicalStart ? { ...rect, width } : { ...rect, x: rect.x + width, width };
}

/** Which side of the canvas the pointer is hugging, as a logical zone. */
function canvasEdgeZone(rect: DOMRect, x: number, y: number, rtl: boolean): Exclude<DropZone, 'center'> | null {
  const distances = [
    { zone: 'top' as const, distance: y - rect.top },
    { zone: 'bottom' as const, distance: rect.bottom - y },
    { zone: (rtl ? 'end' : 'start') as Exclude<DropZone, 'center'>, distance: x - rect.left },
    { zone: (rtl ? 'start' : 'end') as Exclude<DropZone, 'center'>, distance: rect.right - x },
  ].filter((entry) => entry.distance >= 0 && entry.distance <= canvasEdgeBand);
  if (distances.length === 0) return null;
  return distances.sort((left, right) => left.distance - right.distance)[0]?.zone ?? null;
}

/** Where in a tab strip an insertion caret belongs, given the pointer. */
function tabInsertionIndex(strip: HTMLElement, x: number, rtl: boolean): { index: number; caret: Rect } {
  const tabs = [...strip.querySelectorAll<HTMLElement>('[data-dock-tab]')];
  const stripRect = toRect(strip.getBoundingClientRect());
  if (tabs.length === 0) return { index: 0, caret: { x: rtl ? stripRect.x + stripRect.width : stripRect.x, y: stripRect.y, width: 2, height: stripRect.height } };
  for (const [index, tab] of tabs.entries()) {
    const rect = tab.getBoundingClientRect();
    const middle = rect.left + rect.width / 2;
    const before = rtl ? x > middle : x < middle;
    if (before) return { index, caret: { x: rtl ? rect.right : rect.left, y: rect.top, width: 2, height: rect.height } };
  }
  const last = tabs[tabs.length - 1]?.getBoundingClientRect();
  const edge = last === undefined ? stripRect.x : rtl ? last.left : last.right;
  return { index: tabs.length, caret: { x: edge, y: last?.top ?? stripRect.y, width: 2, height: last?.height ?? stripRect.height } };
}

interface ResolveInput {
  readonly x: number;
  readonly y: number;
  readonly rtl: boolean;
  readonly forceFloat: boolean;
  readonly subject: DragSubject;
  readonly state: WorkspaceState;
  readonly canvas: HTMLElement | null;
  readonly frames: ReadonlyMap<string, HTMLElement>;
  readonly tabStrips: ReadonlyMap<string, HTMLElement>;
}

/**
 * Asks the pointer where the document would land. The order is the order of intent: a
 * deliberate float, then a tab strip aimed at directly, then the outer edge of the whole
 * canvas, then whichever pane is under the pointer.
 *
 * A whole window stops after the edge of the canvas. Panes cover every pixel of the
 * workspace, so a window that docked wherever it was released could never simply be
 * moved; docking one is therefore a deliberate act — onto a tab strip, or against an
 * outer edge — and everywhere else it just goes where it was put.
 */
export function resolveDrop(input: ResolveInput): DropResolution | null {
  const { x, y, rtl, forceFloat, subject, state, canvas, frames, tabStrips } = input;
  const canvasRect = canvas?.getBoundingClientRect() ?? null;
  const size = subject.size ?? { width: 760, height: 520 };

  const floatResolution = (): DropResolution => {
    // Window rectangles are stored relative to the canvas, so a window survives a
    // resized browser and a collapsed navigation rail without drifting off screen.
    const origin = canvasRect ?? new DOMRect(0, 0, 1280, 800);
    const width = Math.min(size.width, Math.max(origin.width - 24, 280));
    const height = Math.min(size.height, Math.max(origin.height - 24, 180));
    const rect: Rect = {
      x: clamp(x - subject.grab.x - origin.left, 0, Math.max(origin.width - width, 0)),
      y: clamp(y - subject.grab.y - origin.top, 0, Math.max(origin.height - height, 0)),
      width,
      height,
    };
    const preview: Rect = { x: rect.x + origin.left, y: rect.y + origin.top, width, height };
    return { target: { kind: 'float', rect }, preview, hint: 'float', hostId: null, zone: null, tabIndex: null };
  };

  if (forceFloat) return floatResolution();
  if (canvasRect === null || !contains(canvasRect, x, y)) return floatResolution();

  for (const [hostId, strip] of tabStrips) {
    if (subject.kind === 'window' && subject.id === hostId) continue;
    if (state.maximizedGroupId !== null && state.maximizedGroupId !== hostId && !state.windows.some((window) => window.id === hostId)) continue;
    const rect = strip.getBoundingClientRect();
    if (!contains(rect, x, y)) continue;
    const { index, caret } = tabInsertionIndex(strip, x, rtl);
    const isWindow = state.windows.some((window) => window.id === hostId);
    const target: DockTarget = isWindow
      ? { kind: 'window', windowId: hostId, index }
      : { kind: 'group', groupId: hostId, zone: 'center', index };
    return { target, preview: caret, hint: 'tab', hostId, zone: 'center', tabIndex: index };
  }

  const edge = canvasEdgeZone(canvasRect, x, y, rtl);
  if (edge !== null) {
    const preview = previewForZone(toRect(canvasRect), edge, rtl);
    return { target: { kind: 'root', zone: edge }, preview, hint: 'edge', hostId: null, zone: edge, tabIndex: null };
  }

  if (subject.kind === 'window') return floatResolution();

  // Floating windows sit above the dock, so a pointer over one aims at it, topmost first.
  const stacked = [...state.windows].filter((window) => window.state !== 'minimized').sort((left, right) => right.z - left.z);
  for (const window of stacked) {
    const frame = frames.get(window.id);
    if (frame === undefined) continue;
    const rect = frame.getBoundingClientRect();
    if (!contains(rect, x, y)) continue;
    return {
      target: { kind: 'window', windowId: window.id },
      preview: toRect(rect),
      hint: 'tab',
      hostId: window.id,
      zone: 'center',
      tabIndex: null,
    };
  }

  for (const [hostId, frame] of frames) {
    if (state.windows.some((window) => window.id === hostId)) continue;
    // While one pane fills the canvas the others are still laid out behind it, so they
    // would answer a hit test they are in no position to accept.
    if (state.maximizedGroupId !== null && state.maximizedGroupId !== hostId) continue;
    const rect = frame.getBoundingClientRect();
    if (!contains(rect, x, y)) continue;
    const zone = zoneFromPoint(toRect(rect), x, y, rtl);
    return {
      target: { kind: 'group', groupId: hostId, zone },
      preview: previewForZone(toRect(rect), zone, rtl),
      hint: zone === 'center' ? 'tab' : 'split',
      hostId,
      zone,
      tabIndex: null,
    };
  }

  return null;
}

export function DockDragProvider({ children }: { children: ReactNode }) {
  const state = useWorkspace();
  const { dispatch } = useWorkspaceActions();
  const elements = useDockElements();
  const [drag, setDrag] = useState<DockDragState | null>(null);

  // The listeners are attached once per gesture and read the latest layout through refs,
  // so a resolution never lags a frame behind the arrangement it is being measured against.
  const latest = useRef({ state, elements });
  latest.current = { state, elements };

  const begin = useCallback((event: ReactPointerEvent, subject: DragSubject) => {
    if (event.button !== 0) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let started = false;
    // Held outside React state as well, because the pointer can be released before the
    // frame that rendered the last move, and the drop must use what was last measured.
    let settled: DropResolution | null = null;

    const rtl = document.documentElement.dir === 'rtl';

    const update = (moveEvent: PointerEvent) => {
      if (!started) {
        if (Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < dragThreshold) return;
        started = true;
        document.body.classList.add('is-dock-dragging');
      }
      moveEvent.preventDefault();
      const { state: currentState, elements: currentElements } = latest.current;
      const resolution = resolveDrop({
        x: moveEvent.clientX,
        y: moveEvent.clientY,
        rtl,
        forceFloat: moveEvent.ctrlKey || moveEvent.metaKey,
        subject,
        state: currentState,
        canvas: currentElements.canvas,
        frames: currentElements.frames,
        tabStrips: currentElements.tabStrips,
      });
      settled = resolution;
      // A window is its own preview: it travels under the pointer, and only a drop on a
      // tab strip or against an edge turns the gesture into a dock.
      if (subject.kind === 'window' && resolution?.target.kind === 'float') {
        dispatch({ type: 'moveWindow', windowId: subject.id, rect: resolution.target.rect });
      }
      setDrag({ subject, pointer: { x: moveEvent.clientX, y: moveEvent.clientY }, resolution });
    };

    const finish = (commit: boolean) => {
      window.removeEventListener('pointermove', update);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', onKeyDown, true);
      document.body.classList.remove('is-dock-dragging');
      setDrag(null);
      if (!commit || !started || settled === null) return;
      if (subject.kind === 'panel') dispatch({ type: 'movePanel', panelId: subject.id, target: settled.target });
      else if (settled.target.kind !== 'float') dispatch({ type: 'dockWindow', windowId: subject.id, target: settled.target });
    };

    const release = () => finish(true);
    const cancel = () => finish(false);
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') {
        keyEvent.preventDefault();
        finish(false);
      }
    };

    window.addEventListener('pointermove', update, { passive: false });
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKeyDown, true);
  }, [dispatch]);

  const api = useMemo<DockDragApi>(() => ({
    drag,
    startPanelDrag: (event, subject) => begin(event, { ...subject, kind: 'panel', grab: { x: 120, y: 18 }, size: null }),
    startWindowDrag: (event, subject, rect) => begin(event, {
      ...subject,
      kind: 'window',
      grab: { x: event.clientX - rect.x, y: event.clientY - rect.y },
      size: { width: rect.width, height: rect.height },
    }),
  }), [begin, drag]);

  useEffect(() => () => document.body.classList.remove('is-dock-dragging'), []);

  return <DockDragContext.Provider value={api}>{children}</DockDragContext.Provider>;
}
